import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  ExternalLink,
  Film,
  MoveDown,
  MoveUp,
  Pencil,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type {
  VideoInstructionDto,
  VideoInstructionMime,
  VideoInstructionPresignResponse,
} from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

const ALLOWED_MIME: VideoInstructionMime[] = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-matroska',
];
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDuration(s: number | null): string {
  if (s == null || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/**
 * Read the video file's duration via a hidden <video> element.
 * Returns null on any failure — duration is best-effort, not required.
 */
async function probeDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const cleanup = () => {
      URL.revokeObjectURL(url);
    };
    video.onloadedmetadata = () => {
      const d = Number.isFinite(video.duration) ? Math.round(video.duration) : null;
      cleanup();
      resolve(d && d > 0 ? d : null);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = url;
  });
}

export function VideosPage() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progressPct, setProgressPct] = useState<number | null>(null);

  // Inline edit per row. Only one row in edit mode at a time.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ title: string; description: string }>({
    title: '',
    description: '',
  });

  const listQ = useQuery<{ items: VideoInstructionDto[] }>({
    queryKey: ['admin', 'videos'],
    queryFn: () => api.get('admin/videos').json<{ items: VideoInstructionDto[] }>(),
  });

  const items = listQ.data?.items ?? [];

  const reset = () => {
    setFile(null);
    setTitle('');
    setDescription('');
    setProgressPct(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (!ALLOWED_MIME.includes(f.type as VideoInstructionMime)) {
      pushToast({
        variant: 'error',
        message: t('admin:videos.upload.invalidType', {
          defaultValue: 'Format video nesuportat (acceptăm MP4, WebM, MOV, MKV).',
        }),
      });
      e.target.value = '';
      return;
    }
    if (f.size > MAX_BYTES) {
      pushToast({
        variant: 'error',
        message: t('admin:videos.upload.tooLarge', {
          defaultValue: 'Fișier prea mare (maxim 500 MB).',
        }),
      });
      e.target.value = '';
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!title.trim()) {
      pushToast({
        variant: 'error',
        message: t('admin:videos.upload.titleRequired', {
          defaultValue: 'Titlul este obligatoriu.',
        }),
      });
      return;
    }
    setUploading(true);
    setProgressPct(0);
    try {
      // 1. Ask the API for a presigned PUT URL.
      const presign = await api
        .post('admin/videos/presign', {
          json: {
            fileName: file.name,
            mimeType: file.type as VideoInstructionMime,
            sizeBytes: file.size,
          },
        })
        .json<VideoInstructionPresignResponse>();

      // 2. PUT the file directly to R2 using XHR for upload progress.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presign.url);
        for (const [k, v] of Object.entries(presign.headers)) {
          xhr.setRequestHeader(k, v);
        }
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setProgressPct(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload failed: HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      // 3. Probe duration (best-effort) and confirm.
      const durationSec = await probeDuration(file);
      await api
        .post('admin/videos/confirm', {
          json: {
            storageKey: presign.storageKey,
            title: title.trim(),
            description: description.trim() || undefined,
            mimeType: file.type as VideoInstructionMime,
            sizeBytes: file.size,
            ...(durationSec ? { durationSec } : {}),
          },
        })
        .json<VideoInstructionDto>();

      pushToast({
        variant: 'success',
        message: t('admin:videos.upload.success', {
          defaultValue: 'Video încărcat cu succes.',
        }),
      });
      reset();
      void qc.invalidateQueries({ queryKey: ['admin', 'videos'] });
      // Also invalidate the user-facing /api/videos cache used by docs.
      void qc.invalidateQueries({ queryKey: ['videos'] });
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    } finally {
      setUploading(false);
      setProgressPct(null);
    }
  };

  const reorderMut = useMutation({
    mutationFn: ({ id, displayOrder }: { id: string; displayOrder: number }) =>
      api.patch(`admin/videos/${id}`, { json: { displayOrder } }).json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'videos'] });
      void qc.invalidateQueries({ queryKey: ['videos'] });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const editMut = useMutation({
    mutationFn: ({
      id,
      title: t,
      description: d,
    }: {
      id: string;
      title: string;
      description: string | null;
    }) =>
      api
        .patch(`admin/videos/${id}`, { json: { title: t, description: d } })
        .json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'videos'] });
      void qc.invalidateQueries({ queryKey: ['videos'] });
      setEditingId(null);
      pushToast({
        variant: 'success',
        message: t('admin:videos.edit.success', {
          defaultValue: 'Modificările au fost salvate.',
        }),
      });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const startEdit = (v: VideoInstructionDto) => {
    setEditingId(v.id);
    setEditDraft({ title: v.title, description: v.description ?? '' });
  };
  const cancelEdit = () => {
    setEditingId(null);
  };
  const submitEdit = (id: string) => {
    const t = editDraft.title.trim();
    if (!t) {
      pushToast({
        variant: 'error',
        message: 'Title is required.',
      });
      return;
    }
    editMut.mutate({
      id,
      title: t,
      description: editDraft.description.trim() || null,
    });
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`admin/videos/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'videos'] });
      void qc.invalidateQueries({ queryKey: ['videos'] });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const handleMove = (idx: number, dir: -1 | 1) => {
    const a = items[idx];
    const b = items[idx + dir];
    if (!a || !b) return;
    // Swap their displayOrder values. Two PATCHes; the list invalidates
    // after both resolve.
    void reorderMut.mutateAsync({ id: a.id, displayOrder: b.displayOrder });
    void reorderMut.mutateAsync({ id: b.id, displayOrder: a.displayOrder });
  };

  const handleDelete = (v: VideoInstructionDto) => {
    if (
      !confirm(
        t('admin:videos.delete.confirm', {
          defaultValue: `Ștergi definitiv „${v.title}"? Operația este ireversibilă.`,
          title: v.title,
        }),
      )
    ) {
      return;
    }
    deleteMut.mutate(v.id);
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      pushToast({
        variant: 'success',
        message: t('admin:videos.copy.success', {
          defaultValue: 'Link copiat.',
        }),
      });
    } catch {
      pushToast({
        variant: 'error',
        message: t('admin:videos.copy.failed', {
          defaultValue: 'Nu s-a putut copia linkul.',
        }),
      });
    }
  };

  // Reset the file input value when a new file is picked but cancelled.
  useEffect(() => {
    if (!file && fileInputRef.current) fileInputRef.current.value = '';
  }, [file]);

  return (
    <div className="animate-fade-in mx-auto max-w-3xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
          <Film className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {t('admin:videos.title', { defaultValue: 'Video instrucțiuni' })}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('admin:videos.subtitle', {
              defaultValue:
                'Încarcă video-uri educaționale care apar în pagina Documentație.',
            })}
          </p>
        </div>
      </div>

      {/* Upload card */}
      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <Upload className="h-4 w-4" />
            {t('admin:videos.upload.title', { defaultValue: 'Încarcă video nou' })}
          </h2>
        </CardHeader>
        <CardBody className="space-y-4">
          <FormField
            label={t('admin:videos.upload.titleLabel', { defaultValue: 'Titlu' })}
            htmlFor="video-title"
          >
            <Input
              id="video-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={t('admin:videos.upload.titlePlaceholder', {
                defaultValue: 'Ex: Cum să adaugi un poligon nou',
              })}
              disabled={uploading}
            />
          </FormField>
          <FormField
            label={t('admin:videos.upload.descriptionLabel', {
              defaultValue: 'Descriere (opțional)',
            })}
            htmlFor="video-description"
          >
            <textarea
              id="video-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder={t('admin:videos.upload.descriptionPlaceholder', {
                defaultValue: 'Scurtă descriere a video-ului…',
              })}
              disabled={uploading}
              className="w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </FormField>
          <FormField
            label={t('admin:videos.upload.fileLabel', { defaultValue: 'Fișier video' })}
            htmlFor="video-file"
            hint={t('admin:videos.upload.fileHint', {
              defaultValue: 'MP4, WebM, MOV sau MKV — maxim 500 MB.',
            })}
          >
            <input
              id="video-file"
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
              onChange={handlePickFile}
              disabled={uploading}
              className="block w-full text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-forest-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-forest-700 hover:file:bg-forest-200 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-300 dark:file:bg-forest-900/40 dark:file:text-forest-200"
            />
          </FormField>
          {file ? (
            <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <strong className="text-slate-800 dark:text-slate-100">{file.name}</strong>
              {' · '}
              {fmtBytes(file.size)} · {file.type}
            </div>
          ) : null}
          {progressPct !== null ? (
            <div className="space-y-1">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-full bg-forest-600 transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {progressPct}% — {t('admin:videos.upload.progress', { defaultValue: 'Se încarcă…' })}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={reset}
              disabled={uploading}
            >
              {t('common:action.cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              loading={uploading}
              disabled={!file || !title.trim()}
            >
              <Upload className="mr-1 h-4 w-4" />
              {t('admin:videos.upload.submit', { defaultValue: 'Încarcă' })}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {t('admin:videos.list.title', { defaultValue: 'Video-uri încărcate' })}
            {items.length > 0 ? (
              <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                ({items.length})
              </span>
            ) : null}
          </h2>
        </CardHeader>
        <CardBody className="p-0">
          {listQ.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title={t('admin:videos.list.empty.title', {
                defaultValue: 'Niciun video',
              })}
              description={t('admin:videos.list.empty.description', {
                defaultValue: 'Încărcați primul video folosind formularul de mai sus.',
              })}
            />
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((v, idx) => {
                const isEditing = editingId === v.id;
                return (
                <li key={v.id} className="space-y-3 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="space-y-2">
                          <FormField
                            label={t('admin:videos.upload.titleLabel', {
                              defaultValue: 'Titlu',
                            })}
                            htmlFor={`edit-title-${v.id}`}
                          >
                            <Input
                              id={`edit-title-${v.id}`}
                              value={editDraft.title}
                              onChange={(e) =>
                                setEditDraft((d) => ({ ...d, title: e.target.value }))
                              }
                              maxLength={200}
                              disabled={editMut.isPending}
                              autoFocus
                            />
                          </FormField>
                          <FormField
                            label={t('admin:videos.upload.descriptionLabel', {
                              defaultValue: 'Descriere (opțional)',
                            })}
                            htmlFor={`edit-desc-${v.id}`}
                          >
                            <textarea
                              id={`edit-desc-${v.id}`}
                              value={editDraft.description}
                              onChange={(e) =>
                                setEditDraft((d) => ({
                                  ...d,
                                  description: e.target.value,
                                }))
                              }
                              maxLength={2000}
                              rows={3}
                              disabled={editMut.isPending}
                              className="w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
                            />
                          </FormField>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {v.title}
                          </h3>
                          {v.description ? (
                            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                              {v.description}
                            </p>
                          ) : null}
                        </>
                      )}
                      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-500">
                        {fmtBytes(v.sizeBytes)} · {fmtDuration(v.durationSec)} ·{' '}
                        {v.mimeType} ·{' '}
                        {t('admin:videos.list.uploadedBy', {
                          defaultValue: 'încărcat de {{name}}',
                          name: v.uploadedByName,
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => submitEdit(v.id)}
                            loading={editMut.isPending}
                            disabled={!editDraft.title.trim()}
                            title={t('admin:videos.edit.save', { defaultValue: 'Salvează' })}
                          >
                            <Save className="mr-1 h-3.5 w-3.5" />
                            {t('common:action.save')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={cancelEdit}
                            disabled={editMut.isPending}
                            title={t('common:action.cancel')}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => startEdit(v)}
                            disabled={editMut.isPending}
                            title={t('admin:videos.edit.button', { defaultValue: 'Editează' })}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleMove(idx, -1)}
                            disabled={idx === 0 || reorderMut.isPending}
                            title={t('admin:videos.list.moveUp', { defaultValue: 'Mută sus' })}
                          >
                            <MoveUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleMove(idx, 1)}
                            disabled={idx === items.length - 1 || reorderMut.isPending}
                            title={t('admin:videos.list.moveDown', { defaultValue: 'Mută jos' })}
                          >
                            <MoveDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDelete(v)}
                            disabled={deleteMut.isPending}
                            className="!border-red-200 !text-red-600 hover:!bg-red-50 dark:!border-red-500/30 dark:!text-red-300 dark:hover:!bg-red-500/10"
                            title={t('admin:videos.list.delete', { defaultValue: 'Șterge' })}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <video
                    src={v.publicUrl}
                    controls
                    preload="metadata"
                    className="w-full rounded-md bg-black"
                    style={{ maxHeight: 320 }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={v.publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-forest-700 hover:underline dark:text-forest-300"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {t('admin:videos.list.openLink', { defaultValue: 'Deschide linkul public' })}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopy(v.publicUrl)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
                    >
                      <Copy className="h-3 w-3" />
                      {t('admin:videos.list.copyLink', { defaultValue: 'Copiază linkul' })}
                    </button>
                    <code className="ml-auto truncate rounded bg-slate-50 px-2 py-1 font-mono text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      {v.publicUrl}
                    </code>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
